import React, { useMemo, useState, useEffect } from 'react';
import '../css/OffboardingModal.css';
import { getShortlist } from '../data/shortlist';
import { useAuth } from '../contexts/AuthContext';

const OFF_KEY = 'OFFBOARDED_IDS_v1';
const REQ_KEY = 'OFFBOARDING_REQUESTS_V1';
const TASKS_KEY = 'CRM_TASKS_v1';

export default function OffboardingModal({ isOpen, onClose, roster }) {
  const { currentUser } = useAuth();
  
  // State for API data
  const [shortlistTalents, setShortlistTalents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [showAllTalents, setShowAllTalents] = useState(false);

  // Fetch talents from Shortlist table when modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadShortlistTalents();
    }
  }, [isOpen, currentUser]);

  const loadShortlistTalents = async () => {
    if (!currentUser?.uid) {
      setApiError('User not authenticated');
      return;
    }

    try {
      setLoading(true);
      setApiError('');
      console.log('Loading shortlist talents for user:', currentUser.uid);
      
      const shortlistData = await getShortlist(currentUser.uid);
      console.log('Raw shortlist response:', shortlistData);
      
      // Handle the response structure
      let talents = [];
      if (Array.isArray(shortlistData)) {
        talents = shortlistData;
      } else if (shortlistData && Array.isArray(shortlistData.data)) {
        talents = shortlistData.data;
      }
      
      console.log('Extracted shortlist talents:', talents);
      setShortlistTalents(talents);
    } catch (error) {
      setApiError(error.message || 'Failed to load shortlist talents');
      setShortlistTalents([]);
      console.error('Error loading shortlist talents for offboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build roster from Shortlist data (or provided prop as fallback)
  const allTalents = useMemo(() => {
    const src = Array.isArray(roster) && roster.length ? roster : shortlistTalents;
    console.log('Source data for filtering:', src);
    
    const filtered = (src || [])
      .filter((row) => {
        // Only show talents that are in Deployment stage (unless debugging)
        if (showAllTalents) return true;
        
        // Handle shortlist structure: {talent_id, talent_name, talent_role, stage, status, ...}
        const stage = row.stage || row.currentStage || row.deploymentStage;
        console.log(`Talent ${row.talent_id || row.talentId}: stage="${stage}"`);
        
        return stage === 'Deployment';
      });
    
    console.log('Filtered talents (Deployment stage):', filtered);
    
    return filtered.map((row) => {
        const id = row.talent_id ?? row.talentId ?? row.id ?? Math.random().toString(36).slice(2);
        const fullName = row.talent_name || row.name || row.alias || 'Unknown';
        const role = row.talent_role || row.role || row.position || '—';
        const deptOrProject = row.currentProject || row.department || row.project || '—';
        const startDate = row.startDate || row.hiredDate || row.created_at || '—';
        const status = row.status || row.employmentStatus || 'Active';
        const employmentType = row.engagementModel || row.employmentType || 'EOR Standard';
        const stage = row.stage || row.currentStage || row.deploymentStage || '—';
        const deploymentStatus = row.deploymentStatus || row.currentStatus || row.status || '—';
        
        return { 
          id, 
          fullName, 
          role, 
          deptOrProject, 
          startDate, 
          status, 
          employmentType,
          stage,
          deploymentStatus
        };
      });
  }, [roster, shortlistTalents, showAllTalents]);

  // Persisted "removed" list
  const [offboardedIds, setOffboardedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(OFF_KEY) || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(OFF_KEY, JSON.stringify(offboardedIds)); } catch {}
  }, [offboardedIds]);

  // Search + filtered list
  const [q, setQ] = useState('');
  const visibleTalents = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allTalents
      .filter(t => !offboardedIds.includes(t.id))
      .filter(t =>
        !needle ||
        t.fullName.toLowerCase().includes(needle) ||
        t.role.toLowerCase().includes(needle) ||
        String(t.id).toLowerCase().includes(needle)
      );
  }, [allTalents, offboardedIds, q]);

  // Selection (single)
  const [selectedId, setSelectedId] = useState(null);
  const selectedTalent = useMemo(
    () => visibleTalents.find(t => t.id === selectedId) || null,
    [visibleTalents, selectedId]
  );

  // Exit info (per selection)
  const [exitInfo, setExitInfo] = useState({ 
    finalDate: '', 
    reason: '', 
    otherText: '', 
    notes: '',
    currentStatus: 'Active',
    clientPointOfContact: {
      fullName: '',
      email: '',
      phoneWhatsapp: ''
    },
    engagementModelAlignment: 'Linked to Deployment',
    knowledgeTransferRequired: 'No',
    knowledgeTransferMethod: 'Documentation',
    assetsToBeRetrieved: [],
    assetsOther: '',
    systemAccessRevocation: 'All',
    systemAccessOther: '',
    finalPayrollBenefitsClearance: 'No',
    payrollBenefitsNotes: '',
    exitInterviewRequired: 'No',
    exitInterviewMethod: 'Client-led'
  });
  
  useEffect(() => {
    // Reset form when changing selection
    setExitInfo({ 
      finalDate: '', 
      reason: '', 
      otherText: '', 
      notes: '',
      currentStatus: 'Active',
      clientPointOfContact: {
        fullName: '',
        email: '',
        phoneWhatsapp: ''
      },
      engagementModelAlignment: 'Linked to Deployment',
      knowledgeTransferRequired: 'No',
      knowledgeTransferMethod: 'Documentation',
      assetsToBeRetrieved: [],
      assetsOther: '',
      systemAccessRevocation: 'All',
      systemAccessOther: '',
      finalPayrollBenefitsClearance: 'No',
      payrollBenefitsNotes: '',
      exitInterviewRequired: 'No',
      exitInterviewMethod: 'Client-led'
    });
    setConfirm(false);
    setReplaceYes(false);
    setReplacementTimeline('Flexible');
    setComplianceAddOns({
      alumniEngagement: false,
      massOffboardingSupport: false,
      whiteLabelExit: false
    });
  }, [selectedId]);

  // Replacement (simple yes/no for the single selected role)
  const [replaceYes, setReplaceYes] = useState(false);
  const [replacementTimeline, setReplacementTimeline] = useState('Flexible');

  // Confirmation checkbox
  const [confirm, setConfirm] = useState(false);
  const [complianceAddOns, setComplianceAddOns] = useState({
    alumniEngagement: false,
    massOffboardingSupport: false,
    whiteLabelExit: false
  });

  // Helper functions
  const toggleMultiSelect = (currentArray, setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const Check = ({ label, checked, onChange }) => (
    <label className="obf2-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="obf2-checkmark" /> {label}
    </label>
  );

  // Submit
  const onSubmit = (e) => {
    e.preventDefault();
    if (!selectedTalent) {
      alert('Please select a talent from the list.');
      return;
    }
    if (!exitInfo.finalDate) {
      alert('Please set Final Work Date.');
      return;
    }
    if (!exitInfo.reason) {
      alert('Please choose a Reason for Offboarding.');
      return;
    }
    if (exitInfo.reason === 'other' && !String(exitInfo.otherText || '').trim()) {
      alert('Please specify details for "Other" reason.');
      return;
    }
    if (!exitInfo.clientPointOfContact.fullName.trim()) {
      alert('Please enter Client Point of Contact Full Name.');
      return;
    }
    if (!exitInfo.clientPointOfContact.email.trim()) {
      alert('Please enter Client Point of Contact Email.');
      return;
    }
    if (!exitInfo.clientPointOfContact.phoneWhatsapp.trim()) {
      alert('Please enter Client Point of Contact Phone/WhatsApp.');
      return;
    }
    if (exitInfo.knowledgeTransferRequired === 'Yes' && !exitInfo.knowledgeTransferMethod) {
      alert('Please specify Knowledge Transfer Method.');
      return;
    }
    if (exitInfo.systemAccessRevocation === 'Other' && !exitInfo.systemAccessOther.trim()) {
      alert('Please specify System Access Revocation details.');
      return;
    }
    if (exitInfo.finalPayrollBenefitsClearance === 'Yes' && !exitInfo.payrollBenefitsNotes.trim()) {
      alert('Please add notes for Final Payroll & Benefits Clearance.');
      return;
    }
    if (exitInfo.exitInterviewRequired === 'Yes' && !exitInfo.exitInterviewMethod) {
      alert('Please specify Exit Interview Method.');
      return;
    }
    if (!confirm) {
      alert('Please confirm you want to initiate the offboarding.');
      return;
    }

    // Build payload
    const payload = {
      createdAt: new Date().toISOString(),
      talents: [
        {
          ...selectedTalent,
          exit: exitInfo
        }
      ],
      replacement: { 
        wantReplacement: replaceYes,
        timeline: replacementTimeline
      },
      complianceAddOns,
      routing: {
        triggerTRF: replaceYes,
        enterpriseAE: false
      }
    };

    // Save record + CRM tasks
    try {
      const list = JSON.parse(localStorage.getItem(REQ_KEY) || '[]');
      list.push(payload);
      localStorage.setItem(REQ_KEY, JSON.stringify(list));

      const tasks = JSON.parse(localStorage.getItem(TASKS_KEY) || '[]');
      tasks.push({
        type: 'AE_CALL',
        title: 'Start Offboarding — Client confirmation',
        priority: 'HIGH',
        meta: { count: 1, replacement: { wantReplacement: replaceYes } },
        createdAt: new Date().toISOString()
      });
      if (replaceYes) {
        tasks.push({
          type: 'TRF_DRF_TRIGGER',
          title: 'Replacement requested — start TRF/DRF',
          priority: 'NORMAL',
          meta: { role: selectedTalent.role, forTalentId: selectedTalent.id },
          createdAt: new Date().toISOString()
        });
      }
      localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
    } catch {}

    // Remove from list (persist)
    setOffboardedIds(prev => Array.from(new Set([...prev, selectedTalent.id])));
    setSelectedId(null);

    alert(`Offboarding initiated for ${selectedTalent.fullName}.`);
    onClose?.(); // close modal after submit (optional: keep open by removing this line)
  };

  if (!isOpen) return null;

  return (
    <div className="obf2-overlay" onClick={onClose}>
      <div className="obf2-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="obf2-header">
          <h2 className="obf2-title">Offboarding Request</h2>
          <button className="obf2-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="obf2-body">
          {/* Left: Roster list */}
          <aside className="obf2-left">
            <div className="obf2-filter-info">
            </div>
            <div className="obf2-search">
              <input
                className="obf2-input"
                placeholder="Search by name, role, or ID…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="obf2-list">
              {loading ? (
                <div className="obf2-loading">
                  <div className="obf2-spinner"></div>
                  <span>Loading shortlist talents from database...</span>
                </div>
              ) : apiError ? (
                <div className="obf2-error">
                  <div className="obf2-error-icon">⚠️</div>
                  <div className="obf2-error-text">
                    <strong>Error loading shortlist talents:</strong>
                    <br />
                    {apiError}
                  </div>
                  <button 
                    className="obf2-retry-btn"
                    onClick={loadShortlistTalents}
                  >
                    Retry
                  </button>
                </div>
              ) : visibleTalents.length ? (
                visibleTalents.map((t) => (
                  <button
                    key={t.id}
                    className={`obf2-item ${selectedId === t.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(t.id)}
                    title={`${t.fullName} — ${t.role}`}
                  >
                    <div className="obf2-line">
                      <span className="obf2-id">#{t.id}</span>
                      <span className="obf2-name">{t.fullName}</span>
                    </div>
                    <div className="obf2-role">{t.role}</div>
                  </button>
                ))
              ) : (
                <div className="obf2-empty">
                  {shortlistTalents.length === 0 
                    ? 'No talents found in shortlist.' 
                    : allTalents.length === 0 
                      ? 'No talents found in Deployment stage.' 
                      : 'No talents match your search.'}
                </div>
              )}
            </div>
          </aside>

          {/* Right: Selected card + form */}
          <main className="obf2-right">
            {!selectedTalent ? (
              <div className="obf2-placeholder">
                <div className="obf2-ghost"> Select a talent to offboard</div>
              </div>
            ) : (
              <form className="obf2-form" onSubmit={onSubmit}>
                <section className="obf2-card">
                  <h3 className="obf2-card-title">Selected Talent</h3>
                  <div className="obf2-grid">
                    <div className="obf2-kv">
                      <div className="obf2-k">Talent ID</div>
                      <div className="obf2-v">#{selectedTalent.id}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Full Name</div>
                      <div className="obf2-v">{selectedTalent.fullName}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Role / Position</div>
                      <div className="obf2-v">{selectedTalent.role}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Dept / Project</div>
                      <div className="obf2-v">{selectedTalent.deptOrProject}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Start Date</div>
                      <div className="obf2-v">{selectedTalent.startDate}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Status</div>
                      <div className="obf2-v">{selectedTalent.status}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Employment Type</div>
                      <div className="obf2-v">{selectedTalent.employmentType}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Stage</div>
                      <div className="obf2-v">{selectedTalent.stage}</div>
                    </div>
                    <div className="obf2-kv">
                      <div className="obf2-k">Deployment Status</div>
                      <div className="obf2-v">{selectedTalent.deploymentStatus}</div>
                    </div>
                  </div>
                </section>

                <section className="obf2-card">
                  <h3 className="obf2-card-title">Section 1: Talent Selection</h3>
                  
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Current Status <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.currentStatus}
                        onChange={(e) => setExitInfo(s => ({ ...s, currentStatus: e.target.value }))}
                        required
                      >
                        <option value="Active">Active</option>
                        <option value="Probationary">Probationary</option>
                        <option value="Regular">Regular</option>
                        <option value="Contractual">Contractual</option>
                      </select>
                    </div>

                    <div className="obf2-field">
                      <label className="obf2-label">Engagement Model Alignment <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.engagementModelAlignment}
                        onChange={(e) => setExitInfo(s => ({ ...s, engagementModelAlignment: e.target.value }))}
                        required
                      >
                        <option value="Linked to Deployment">Linked to Deployment</option>
                        <option value="Linked to Full Suite">Linked to Full Suite</option>
                        <option value="Standalone Offboarding">Standalone Offboarding</option>
                      </select>
                    </div>
                  </div>

                  <div className="obf2-section-title">Client Point of Contact for Offboarding</div>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Full Name <span className="req">*</span></label>
                      <input
                        className="obf2-input"
                        placeholder="Enter full name"
                        value={exitInfo.clientPointOfContact.fullName}
                        onChange={(e) => setExitInfo(s => ({ 
                          ...s, 
                          clientPointOfContact: { ...s.clientPointOfContact, fullName: e.target.value }
                        }))}
                        required
                      />
                    </div>

                    <div className="obf2-field">
                      <label className="obf2-label">Email <span className="req">*</span></label>
                      <input
                        type="email"
                        className="obf2-input"
                        placeholder="Enter email address"
                        value={exitInfo.clientPointOfContact.email}
                        onChange={(e) => setExitInfo(s => ({ 
                          ...s, 
                          clientPointOfContact: { ...s.clientPointOfContact, email: e.target.value }
                        }))}
                        required
                      />
                    </div>

                    <div className="obf2-field">
                      <label className="obf2-label">Phone/WhatsApp <span className="req">*</span></label>
                      <input
                        type="tel"
                        className="obf2-input"
                        placeholder="Enter phone number"
                        value={exitInfo.clientPointOfContact.phoneWhatsapp}
                        onChange={(e) => setExitInfo(s => ({ 
                          ...s, 
                          clientPointOfContact: { ...s.clientPointOfContact, phoneWhatsapp: e.target.value }
                        }))}
                        required
                      />
                    </div>
                  </div>
                </section>

                <section className="obf2-card">
                  <h3 className="obf2-card-title">Exit Information</h3>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Final Work Date <span className="req">*</span></label>
                      <input
                        type="date"
                        className="obf2-input"
                        value={exitInfo.finalDate}
                        onChange={(e) => setExitInfo(s => ({ ...s, finalDate: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="obf2-field">
                      <label className="obf2-label">Reason for Offboarding <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.reason}
                        onChange={(e) => setExitInfo(s => ({ ...s, reason: e.target.value }))}
                        required
                      >
                        <option value="" disabled>Choose…</option>
                        <option value="performance">Performance Concerns</option>
                        <option value="completed">Project Completed</option>
                        <option value="role_removed">Role No Longer Required</option>
                        <option value="other">Other (please specify)</option>
                      </select>
                    </div>

                    {exitInfo.reason === 'other' && (
                      <div className="obf2-field">
                        <label className="obf2-label">Specify Reason <span className="req">*</span></label>
                        <input
                          className="obf2-input"
                          placeholder="Describe the reason…"
                          value={exitInfo.otherText}
                          onChange={(e) => setExitInfo(s => ({ ...s, otherText: e.target.value }))}
                          required
                        />
                      </div>
                    )}

                    <div className="obf2-field -full">
                      <label className="obf2-label">Additional Notes (optional)</label>
                      <textarea
                        className="obf2-textarea"
                        rows={3}
                        placeholder="Anything important about handover, access removal, etc."
                        value={exitInfo.notes}
                        onChange={(e) => setExitInfo(s => ({ ...s, notes: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="obf2-section-title">Knowledge Transfer</div>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Knowledge Transfer Required? <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.knowledgeTransferRequired}
                        onChange={(e) => setExitInfo(s => ({ ...s, knowledgeTransferRequired: e.target.value }))}
                        required
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>

                    {exitInfo.knowledgeTransferRequired === 'Yes' && (
                      <div className="obf2-field">
                        <label className="obf2-label">Knowledge Transfer Method <span className="req">*</span></label>
                        <select
                          className="obf2-select"
                          value={exitInfo.knowledgeTransferMethod}
                          onChange={(e) => setExitInfo(s => ({ ...s, knowledgeTransferMethod: e.target.value }))}
                          required
                        >
                          <option value="Documentation">Documentation</option>
                          <option value="Shadowing">Shadowing</option>
                          <option value="Recorded sessions">Recorded sessions</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="obf2-section-title">Assets to be Retrieved</div>
                  <div className="obf2-fields">
                    <div className="obf2-field -full">
                      <label className="obf2-label">Assets to be Retrieved</label>
                      <div className="obf2-checklist">
                        {['Laptop', 'PC', 'Mobile Device', 'Software License', 'Access Credentials', 'ID/Badge'].map((asset) => (
                          <Check
                            key={asset}
                            label={asset}
                            checked={exitInfo.assetsToBeRetrieved.includes(asset)}
                            onChange={(checked) => {
                              if (checked) {
                                setExitInfo(s => ({ ...s, assetsToBeRetrieved: [...s.assetsToBeRetrieved, asset] }));
                              } else {
                                setExitInfo(s => ({ ...s, assetsToBeRetrieved: s.assetsToBeRetrieved.filter(item => item !== asset) }));
                              }
                            }}
                          />
                        ))}
                        <Check
                          label="Other"
                          checked={exitInfo.assetsToBeRetrieved.includes('Other')}
                          onChange={(checked) => {
                            if (checked) {
                              setExitInfo(s => ({ ...s, assetsToBeRetrieved: [...s.assetsToBeRetrieved, 'Other'] }));
                            } else {
                              setExitInfo(s => ({ ...s, assetsToBeRetrieved: s.assetsToBeRetrieved.filter(item => item !== 'Other') }));
                            }
                          }}
                        />
                      </div>

                      {exitInfo.assetsToBeRetrieved.includes('Other') && (
                        <div className="obf2-field">
                          <label className="obf2-label">Please specify other assets</label>
                          <input
                            className="obf2-input"
                            placeholder="Enter other assets to be retrieved..."
                            value={exitInfo.assetsOther}
                            onChange={(e) => setExitInfo(s => ({ ...s, assetsOther: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="obf2-section-title">System Access Revocation</div>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">System Access Revocation Needed <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.systemAccessRevocation}
                        onChange={(e) => setExitInfo(s => ({ ...s, systemAccessRevocation: e.target.value }))}
                        required
                      >
                        <option value="Email">Email</option>
                        <option value="CRM">CRM</option>
                        <option value="Cloud Storage">Cloud Storage</option>
                        <option value="Project Management Tools">Project Management Tools</option>
                        <option value="All">All</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {exitInfo.systemAccessRevocation === 'Other' && (
                      <div className="obf2-field">
                        <label className="obf2-label">Please specify system access details</label>
                        <input
                          className="obf2-input"
                          placeholder="Enter system access details..."
                          value={exitInfo.systemAccessOther}
                          onChange={(e) => setExitInfo(s => ({ ...s, systemAccessOther: e.target.value }))}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="obf2-section-title">Final Payroll & Benefits</div>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Final Payroll & Benefits Clearance <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.finalPayrollBenefitsClearance}
                        onChange={(e) => setExitInfo(s => ({ ...s, finalPayrollBenefitsClearance: e.target.value }))}
                        required
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>

                    {exitInfo.finalPayrollBenefitsClearance === 'Yes' && (
                      <div className="obf2-field -full">
                        <label className="obf2-label">Payroll & Benefits Notes <span className="req">*</span></label>
                        <textarea
                          className="obf2-textarea"
                          rows={3}
                          placeholder="Specify payroll and benefits clearance details..."
                          value={exitInfo.payrollBenefitsNotes}
                          onChange={(e) => setExitInfo(s => ({ ...s, payrollBenefitsNotes: e.target.value }))}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="obf2-section-title">Exit Interview</div>
                  <div className="obf2-fields">
                    <div className="obf2-field">
                      <label className="obf2-label">Exit Interview Required? <span className="req">*</span></label>
                      <select
                        className="obf2-select"
                        value={exitInfo.exitInterviewRequired}
                        onChange={(e) => setExitInfo(s => ({ ...s, exitInterviewRequired: e.target.value }))}
                        required
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>

                    {exitInfo.exitInterviewRequired === 'Yes' && (
                      <div className="obf2-field">
                        <label className="obf2-label">Exit Interview Method <span className="req">*</span></label>
                        <select
                          className="obf2-select"
                          value={exitInfo.exitInterviewMethod}
                          onChange={(e) => setExitInfo(s => ({ ...s, exitInterviewMethod: e.target.value }))}
                          required
                        >
                          <option value="Client-led">Client-led</option>
                          <option value="Exodia-led">Exodia-led</option>
                          <option value="Both">Both</option>
                        </select>
                      </div>
                    )}
                  </div>
                </section>

                <section className="obf2-card">
                  <h3 className="obf2-card-title">Section 3: Optional Replacement Request</h3>
                  <label className="obf2-switch">
                    <input
                      type="checkbox"
                      checked={replaceYes}
                      onChange={(e) => setReplaceYes(e.target.checked)}
                    />
                    <span className="obf2-switch-ui" />
                    <span className="obf2-switch-label">Request a replacement for this role</span>
                  </label>
                  {replaceYes && (
                    <div className="obf2-fields">
                      <div className="obf2-field">
                        <label className="obf2-label">Timeline for Replacement <span className="req">*</span></label>
                        <select
                          className="obf2-select"
                          value={replacementTimeline}
                          onChange={(e) => setReplacementTimeline(e.target.value)}
                          required
                        >
                          <option value="Immediate">Immediate</option>
                          <option value="Within 2 weeks">Within 2 weeks</option>
                          <option value="Within 1 month">Within 1 month</option>
                          <option value="Flexible">Flexible</option>
                        </select>
                      </div>
                      <p className="obf2-hint">We'll trigger the TRF/DRF flow and notify the AE team.</p>
                    </div>
                  )}
                </section>

                <section className="obf2-card">
                  <h3 className="obf2-card-title">Section 4: Confirmation</h3>
                  
                  <div className="obf2-section-title">Compliance Add-Ons (Optional)</div>
                  <div className="obf2-checklist">
                    <Check
                      label="Alumni Engagement"
                      checked={complianceAddOns.alumniEngagement}
                      onChange={(checked) => setComplianceAddOns(s => ({ ...s, alumniEngagement: checked }))}
                    />
                    <Check
                      label="Mass Offboarding Support"
                      checked={complianceAddOns.massOffboardingSupport}
                      onChange={(checked) => setComplianceAddOns(s => ({ ...s, massOffboardingSupport: checked }))}
                    />
                    <Check
                      label="White Label Exit"
                      checked={complianceAddOns.whiteLabelExit}
                      onChange={(checked) => setComplianceAddOns(s => ({ ...s, whiteLabelExit: checked }))}
                    />
                  </div>

                  <div className="obf2-section-title">Final Confirmation</div>
                  <label className="obf2-check">
                    <input
                      type="checkbox"
                      checked={confirm}
                      onChange={(e) => setConfirm(e.target.checked)}
                      required
                    />
                    <span className="obf2-checkmark" />
                    <span className="obf2-checktext">
                      I confirm that I want to initiate the offboarding process for the selected talent.
                      Exodia will handle all clearance, compliance, and documentation.
                    </span>
                  </label>
                </section>

                <div className="obf2-footer">
                  <button type="button" className="obf2-btn obf2-btn-secondary" onClick={onClose}>Cancel</button>
                  <button type="submit" className="obf2-btn obf2-btn-primary" disabled={!selectedTalent}>
                    Start Offboarding
                  </button>
                </div>
              </form>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
